/**
 * @fileoverview Store API service with TanStack Query hooks.
 * @module features/settings/api/storeService
 */

import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import {
  StoreArraySchema,
  StoreSchema,
  type Store,
  type CreateStoreRequest,
  type UpdateStoreRequest,
  type ConnectionMode,
} from '../schemas/storeSchema'

// ============ Query Keys ============

export const STORES_QUERY_KEY = ['stores'] as const
export const STORE_QUERY_KEY = (id: number) => ['stores', id] as const

// ============ API Functions ============

/**
 * Fetches all stores for the organization.
 */
export async function getStores(): Promise<Store[]> {
  const res = await apiClient.get('/stores')
  return StoreArraySchema.parse(res.data)
}

/**
 * Fetches a single store by ID.
 */
export async function getStore(id: number): Promise<Store> {
  const res = await apiClient.get(`/stores/${id}`)
  return StoreSchema.parse(res.data)
}

/**
 * Creates a new store.
 */
export async function createStore(request: CreateStoreRequest, force = false): Promise<Store> {
  const url = force ? '/stores?force=true' : '/stores'
  const res = await apiClient.post(url, request)
  return StoreSchema.parse(res.data)
}

/**
 * Updates an existing store.
 */
export async function updateStore(id: number, request: UpdateStoreRequest): Promise<Store> {
  const res = await apiClient.put(`/stores/${id}`, request)
  return StoreSchema.parse(res.data)
}

/**
 * Deletes a store.
 */
export async function deleteStore(id: number): Promise<void> {
  await apiClient.delete(`/stores/${id}`)
}

// ============ React Query Hooks ============

/**
 * Hook to fetch all stores.
 */
export function useStoresQuery(): UseQueryResult<Store[], Error> {
  return useQuery({
    queryKey: STORES_QUERY_KEY,
    queryFn: getStores,
    staleTime: 30_000, // 30 seconds
  })
}

/**
 * Hook to fetch a single store.
 */
export function useStoreQuery(id: number): UseQueryResult<Store, Error> {
  return useQuery({
    queryKey: STORE_QUERY_KEY(id),
    queryFn: () => getStore(id),
    staleTime: 30_000,
  })
}

/**
 * Hook to create a new store.
 */
export function useCreateStoreMutation(): UseMutationResult<
  Store,
  Error,
  { request: CreateStoreRequest; force?: boolean }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ request, force }) => createStore(request, force),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY })
    },
  })
}

/**
 * Hook to update a store.
 */
export function useUpdateStoreMutation(): UseMutationResult<
  Store,
  Error,
  { id: number; request: UpdateStoreRequest }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, request }) => updateStore(id, request),
    onSuccess: async (_, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: STORE_QUERY_KEY(id) }),
      ])
    },
  })
}

/**
 * Hook to delete a store.
 */
export function useDeleteStoreMutation(): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => deleteStore(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY })
    },
  })
}

// ============ Connection Mode API ============

/**
 * Updates the connection mode for a store.
 * - DIRECT: Orders are synced directly from the platform
 * - ATTRIBUTION_ONLY: Only attribution/pixel tracking, orders via OMS
 */
export async function updateConnectionMode(id: number, connectionMode: ConnectionMode): Promise<Store> {
  const res = await apiClient.patch(`/stores/${id}/connection-mode`, { connectionMode })
  return StoreSchema.parse(res.data)
}

/**
 * Hook to update store connection mode.
 */
export function useUpdateConnectionModeMutation(): UseMutationResult<
  Store,
  Error,
  { id: number; connectionMode: ConnectionMode }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, connectionMode }) => updateConnectionMode(id, connectionMode),
    onSuccess: async (_, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: STORE_QUERY_KEY(id) }),
        // Also invalidate integrations status as connection mode affects display
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey.includes('integrations-status'),
        }),
      ])
    },
  })
}
